import { RestApi } from "./RestApi";
import { CacheStrategy } from "./CacheApi";
import { mapApiToWishlist } from "../state/StateUtil";

import type { RestApiParams } from "./RestApi";
import type {
  StorefrontApi,
  Product as StorefrontProduct,
} from "./StorefrontApi";
import type { ShopifyProduct } from "./ShopifyApi";
import { onVisitorConsent, type VisitorConsent } from "./consent-api";

export interface WishlistApiParams extends Omit<RestApiParams, "name"> {
  shop: string;
  sessionId?: string;
  customerId?: string;
  trackingSource?: string;
  storefrontApi: StorefrontApi;
}

export class WishlistApi extends RestApi {
  #storefrontApi: StorefrontApi;
  #visitorConsent?: VisitorConsent;

  constructor(params: WishlistApiParams) {
    super({
      ...params,
      name: "wishlist",
    });

    this.#storefrontApi = params.storefrontApi;

    this.headers = {
      "content-type": "application/json",
      // "x-appmate-api": "2023-07",
    };

    if (params.shop) {
      this.headers["x-appmate-shp"] = params.shop;
    }

    if (params.sessionId) {
      this.headers["x-appmate-sid"] = params.sessionId;
    }

    if (params.customerId) {
      this.headers["x-appmate-cid"] = params.customerId.toString();
    }

    if (params.trackingSource) {
      // TODO: Needs to update on pushState navigations
      this.headers["x-appmate-src"] = params.trackingSource;
    }

    onVisitorConsent((visitorConsent) => {
      this.#visitorConsent = visitorConsent;

      this.headers["x-appmate-vca"] =
        visitorConsent.analyticsAllowed.toString();
      this.headers["x-appmate-vcm"] =
        visitorConsent.marketingAllowed.toString();

      // this.headers["x-appmate-vcp"] = visitorConsent.preferencesAllowed ? "1" : "0";
      // this.headers["x-appmate-vcs"] = visitorConsent.saleOfDataAllowed ? "1" : "0";
    });
  }

  public get hasSession() {
    return !!this.headers["x-appmate-sid"] || !!this.headers["x-appmate-cid"];
  }

  public get hasCustomer() {
    return !!this.headers["x-appmate-cid"];
  }

  setSessionId(sessionId: string) {
    this.headers["x-appmate-sid"] = sessionId;
  }

  clearSessionId() {
    delete this.headers["x-appmate-sid"];
  }

  public async loadWishlist({
    wishlistId,
    cacheStrategy = CacheStrategy.Medium,
  }: LoadWishlistParams) {
    const isMine = wishlistId === "mine";

    if (isMine && !this.hasSession) {
      return {
        wishlist: {
          id: wishlistId,
          isMine: true,
          numItems: 0,
          items: [],
        } as Wishlist,
      };
    }

    return this.get<{ wishlist: ApiWishlist }>({
      path: `/wishlists/${wishlistId}`,
      cacheStrategy,
    }).then(({ wishlist }) =>
      Promise.all(
        wishlist.items.map((item) =>
          this.#storefrontApi.loadProduct({
            productId: item.productId,
            cacheStrategy,
          }),
        ),
      ).then((products) => ({
        wishlist: mapApiToWishlist(
          wishlist,
          products.filter((product) => !!product) as StorefrontProduct[],
        ),
      })),
    );
  }

  private _pendingSessionId?: Promise<{
    sessionId?: string;
  }>;

  public addItem({
    productId,
    variantId,
  }: {
    productId: number | string;
    variantId?: number | string;
  }): Promise<{
    wishlistItem: ApiWishlistItemRef;
    sessionId?: string;
  }> {
    if (typeof productId === "string") {
      productId = parseInt(productId);
    }
    if (typeof variantId === "string") {
      variantId = parseInt(variantId);
    }

    if (!this.hasSession && this._pendingSessionId) {
      return this._pendingSessionId.then(() =>
        this.addItem({
          productId,
          variantId,
        }),
      );
    }

    const request = this.post<{
      wishlistItem: ApiWishlistItemRef;
      sessionId?: string;
    }>({
      path: `/wishlists/mine/items`,
      body: {
        productId,
        variantId,
      },
    }).then((result) => {
      if (result.sessionId) {
        this.setSessionId(result.sessionId);
        this._pendingSessionId = undefined;
      }
      return result;
    });

    if (!this.hasSession) {
      this._pendingSessionId = request;
    }

    return request;
  }

  public updateItem({
    wishlistItemId,
    changes,
  }: {
    wishlistItemId: string;
    changes: {
      variantId: number;
    };
  }) {
    return this.put<{ wishlistItem: ApiWishlistItem }>({
      path: `/wishlists/mine/items`,
      body: {
        changes: {
          ...changes,
          wishlistItemId,
        },
      },
    });
  }

  public removeItem({
    wishlistId,
    wishlistItemId,
  }: {
    wishlistId: string;
    wishlistItemId: string;
  }) {
    return this.delete<{ wishlistItem: ApiWishlistItem }>({
      path: `/wishlists/${wishlistId}/items`,
      body: {
        wishlistItemId,
      },
    });
  }

  public clearWishlist() {
    return this.delete<void>({
      path: `/wishlists/mine`,
    });
  }
}

export interface LoadWishlistParams {
  wishlistId: string;
  cacheStrategy?: CacheStrategy;
}

export interface LoadWishlistItemParams {
  wishlistId: string;
  wishlistItemId: string;
}

export interface ApiWishlist {
  id: string;
  publicId?: string;
  isMine: boolean;
  items: ApiWishlistItemRef[];
}

export interface ApiWishlistItemRef {
  id: string;
  productId: number;
  variantId?: number;
}

export interface ApiWishlistItem {
  id: string;
  product: {
    id: number;
  };
  selectedVariantId: number | null;
}

export interface Wishlist {
  id: string;
  publicId?: string;
  isMine: boolean;
  numItems: number;
  items: WishlistItem[];
}

export interface WishlistItem {
  id: string;
  product: ShopifyProduct | PlaceholderProduct;
  variantId?: number;
}

export interface PlaceholderProduct {
  id: number;
  handle: string;
  variants: [];
}
